
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../i18n";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import { Schemes, Utils } from "../../../../utils";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import * as ReactRouterDom from "react-router-dom";
import { Button, ButtonManageRef, Classname, FormControl, NoseurElement, NoseurObject, ObjectHelper, Scheme, StringHelper, TextInput, useSubscription } from "@rivtn/noseur";
import { BaseService, IMember, IPaymentMethod, IPlatformFeatureGroup, IPlatformPlan, MemberService, OmneHostPlan, PlatformService, SanitizedResponse } from "../../../../services";
import { Elements } from "../../../shared/Elements";
import { PlanUpSellEvent } from "@syllektis/oya-gbewa-react";

export interface ManagePlanProps {
    profile: IMember;
}

function ManagePlan(props: ManagePlanProps) {

    let { profile } = props;
    const memberService = MemberService.getInstance();
    const platformService = PlatformService.getInstance();
    const updateProfile = useSubscription("OMNEHOST.PROFILE.UPDATED");
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const labels = i18nManager.Labels.account.plan_and_billing.manage_plan;
    const [platformPlans, setPlatformPlans] = React.useState<IPlatformPlan[]>();
    const [paymentMethods, setPaymentMethods] = React.useState<IPaymentMethod[]>();
    const [fetchingPlansAndFeature, setFetchingPlansAndFeature] = React.useState(true);
    const planActionButtonRef = React.useRef<NoseurObject<(ButtonManageRef | null)[]>>({});
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [platformFeatureGroups, setPlatformFeatureGroups] = React.useState<IPlatformFeatureGroup[]>();
    const [duration, setDuration] = React.useState<"MONTHLY" | "YEARLY">(searchParams.get("duration") as any ?? profile.extra_data?.current_plan_duration ?? "MONTHLY");

    React.useEffect(() => {
        fetchPlatformPlans();
        platformService.queryFeatures().then(({ apiResponse }: SanitizedResponse<IPlatformFeatureGroup>) => {
            setPlatformFeatureGroups(apiResponse.data!.content);
        }).catch(BaseService.reportError);
        memberService.queryPaymentMethods(profile.extra_data.currency_code, { page: 1, size: 100, sort: "created_at,desc" }).then(({ apiResponse }: SanitizedResponse<IPaymentMethod>) => {
            setPaymentMethods(apiResponse.data?.content);
        }).catch(BaseService.reportError);
        const ps = searchParams.get("ps");
        const pn = searchParams.get("pn");
        const pd = searchParams.get("pd");
        const pt = searchParams.get("pt");
        if (ps && pn && (pt === "upgrade" || pt === "downgrade")) {
            const failed = "success" !== ps;
            let message = (failed ? labels.dialog[pt]?.failure_message : labels.dialog[pt]?.success_message);
            Siren.alert(message.replace("${plan}", pn).replace("${duration}", pd ?? ""), (failed ? { scheme: Scheme.DANGER } : undefined));
            searchParams.delete("ps"); searchParams.delete("pn"); searchParams.delete("pt"); searchParams.delete("pd");
        }
    }, []);

    React.useEffect(() => {
        Utils.updateSearchParams("duration", duration, setSearchParams);
    }, [duration]);

    React.useEffect(() => {
        if (fetchingPlansAndFeature && !!platformPlans?.length && !!platformFeatureGroups?.length) {
            setFetchingPlansAndFeature(false);
        }
    }, [platformPlans, platformFeatureGroups]);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.plan_and_billing.title} - {labels.title}</span>
            <span className="desc">{labels.desc}</span>
        </div>
        {fetchingPlansAndFeature && (<div style={{ margin: "90px 110px", display: "flex" }}>
            <img alt={"loading plans and features"} src={LoadingGif} style={{ width: 50, margin: "auto" }} />
        </div>)}
        {!fetchingPlansAndFeature && (<div className="manage-plan">
            <div className="duration-toggle">
                <Button borderless text={labels.duration.monthly} className={Classname.build({ active: (duration === "MONTHLY") })} onClick={() => setDuration("MONTHLY")} />
                <Button borderless text={labels.duration.yearly} className={Classname.build({ active: (duration === "YEARLY") })} onClick={() => setDuration("YEARLY")} />
            </div>
            <div className="plans-features">
                <div>
                    <div />
                    {platformPlans?.filter((platformPlan) => {
                        if (duration === "MONTHLY" && !platformPlan.can_bill_yearly) return false;
                        if (duration === "MONTHLY" && !platformPlan.can_bill_monthly) return false;
                        return true;
                    }).map((platformPlan, index) => {
                        const isCurrentPlanDuration = profile.extra_data.current_plan_duration === duration;
                        const isCurrentPlan = platformPlan.code === profile.plan && isCurrentPlanDuration;
                        const { amount, percentageOff, durationIsMonthly, amountBilledMonthly } = platformPlan.__calculated_amounts[duration === "MONTHLY" ? "monthly" : "yearly"];
                        const downgradePlan = (profile.extra_data?.current_plan_amount > amount)
                            || platformPlans.findIndex((p) => p.code === profile.plan && isCurrentPlanDuration) > index;
                        let actionLabel = (isCurrentPlan
                            ? labels.current_plan
                            : (downgradePlan
                                ? labels.downgrade
                                : labels.upgrade));
                        return (<div key={platformPlan.code}>
                            <div className="plan-detail">
                                <span className="name">{platformPlan.name} {!isNaN(percentageOff) && (<span className="noseur-pill">{percentageOff.toFixed(1)}% {labels.off}</span>)}</span>
                                <span className="amount"><span className="curr" style={{ marginRight: 5, marginTop: 5 }}>{i18nManager.getCurrencySign(profile.extra_data?.currency_code)} </span>{StringHelper.toPresentableMoneyValue(amount)}</span>
                                <span className="billed-interval">{StringHelper.toPresentableMoneyValue(amountBilledMonthly)} {durationIsMonthly ? labels.per_month_billed_monthly : labels.per_month_billed_yearly}</span>
                                <Button scheme={downgradePlan && !isCurrentPlan ? Scheme.DANGER : Schemes.OMNEHOST} disabled={isCurrentPlan} text={actionLabel} fill
                                    loadingProps={Elements.BUTTON_LOADING_PROPS} onClick={() => changePlan(platformPlan, amount, (downgradePlan ? "DOWNGRADE" : "UPGRADE"))} manageRef={(m) => {
                                        if (!(platformPlan.code in planActionButtonRef.current)) planActionButtonRef.current[platformPlan.code] = [];
                                        planActionButtonRef.current[platformPlan.code].push(m);
                                    }} />
                            </div>
                        </div>);
                    })}
                </div>
                {platformFeatureGroups?.filter((g) => g.display)?.map((platformFeatureGroup, index) => {
                    return (<React.Fragment key={platformFeatureGroup.code}>
                        <div className={Classname.build((index > 0 ? "feature-group" : null))}>
                            <b>{platformFeatureGroup.label}</b>
                        </div>
                        {platformFeatureGroup.features.filter((f) => f.display)?.map((feature) => {
                            return (<div key={feature.code}>
                                <span>{feature.label}</span>
                                {platformPlans?.filter((platformPlan) => {
                                    if (duration === "MONTHLY" && !platformPlan.can_bill_yearly) return false;
                                    if (duration === "MONTHLY" && !platformPlan.can_bill_monthly) return false;
                                    return true;
                                }).map((platformPlan) => {

                                    const value = platformPlan.features_mapping[feature.code] ?? "";
                                    const valueType = typeof value;
                                    return (<span key={platformPlan.code + feature.code}>
                                        {(valueType === "number" || valueType === "string") && value}
                                        {value === true && (<i style={{ color: "#0FA883" }} className="fa fa-check" />)}
                                        {value === false && (<i style={{ color: "#FF7171" }} className="fa fa-times" />)}
                                    </span>);
                                })}
                            </div>);
                        })}
                    </React.Fragment>);
                })}
                <div style={{ marginTop: 30 }}>
                    <div />
                    {platformPlans?.filter((platformPlan) => {
                        if (duration === "MONTHLY" && !platformPlan.can_bill_yearly) return false;
                        if (duration === "MONTHLY" && !platformPlan.can_bill_monthly) return false;
                        return true;
                    }).map((platformPlan, index) => {
                        const isCurrentPlanDuration = profile.extra_data.current_plan_duration === duration;
                        const isCurrentPlan = platformPlan.code === profile.plan && isCurrentPlanDuration;
                        const downgradePlan = (profile.extra_data?.current_plan_amount > platformPlan.__calculated_amounts[duration === "MONTHLY" ? "monthly" : "yearly"].amount)
                            || platformPlans.findIndex((p) => p.code === profile.plan && isCurrentPlanDuration) > index;
                        let actionLabel = (isCurrentPlan
                            ? labels.current_plan
                            : (downgradePlan
                                ? labels.downgrade
                                : labels.upgrade));

                        return (<div key={platformPlan.code}>
                            <div className="plan-detail">
                                <Button fill scheme={downgradePlan && !isCurrentPlan ? Scheme.DANGER : Schemes.OMNEHOST} disabled={isCurrentPlan} text={actionLabel}
                                    loadingProps={Elements.BUTTON_LOADING_PROPS}
                                    onClick={() => changePlan(platformPlan, platformPlan.__calculated_amounts[duration === "MONTHLY" ? "monthly" : "yearly"].amount, (downgradePlan ? "DOWNGRADE" : "UPGRADE"))}
                                    manageRef={(m) => {
                                        if (!(platformPlan.code in planActionButtonRef.current)) planActionButtonRef.current[platformPlan.code] = [];
                                        planActionButtonRef.current[platformPlan.code].push(m);
                                    }} />
                            </div>
                        </div>);
                    })}
                </div>
            </div>
        </div>)}
    </div>);

    function fetchPlatformPlans() {
        platformService.queryPlans(profile.extra_data?.currency_code ?? "NOT_SET").then(({ apiResponse }: SanitizedResponse<IPlatformPlan>) => {
            setPlatformPlans(apiResponse.data!.content);
        }).catch(BaseService.reportError);
    }

    function changePlan(platformPlan: IPlatformPlan, amount: number, change: "UPGRADE" | "DOWNGRADE") {
        planActionButtonRef.current[platformPlan.code]?.forEach((m) => m?.setLoadingState(true));
        ChangePlan.to({
            profile,
            duration,
            paymentMethods,
            activeDialogMutableRoot,
            onSuccess: fetchPlatformPlans,
            updateProfile: (_profile) => {
                profile = _profile;
                updateProfile(profile);
            },
            onClose: () => {
                planActionButtonRef.current[platformPlan.code]?.forEach((m) => m?.setLoadingState(false));
            },
        }, change, platformPlan, amount, "info");
    }

}

export class ChangePlan {

    static MemberService = MemberService.getInstance();
    static PlatformService = PlatformService.getInstance();
    static Labels = i18nManager.Labels.account.plan_and_billing.manage_plan;

    static upSell({ profile, planName, capability, cancelAction }: PlanUpSellEvent & { profile: IMember; cancelAction?: () => void; }) {
        const action = Utils.toWords(capability);
        const labels = i18nManager.Labels.up_sell;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: labels.upgrade_plan,
            confirmScheme: Schemes.GOLDEN,
            cancelLabel: (cancelAction ? i18nManager.Labels.common.back : undefined),
            content: (<div className="plan-change" style={{ margin: "15px 0px 5px 0px" }}>
                <span dangerouslySetInnerHTML={{ __html: labels.info.replaceAll("'${action}'", (action ? `'${action}'` : "")).replaceAll("${plan}", planName) }} />
                <a href={labels.article.location} target="_blank" rel={"noreferrer"} style={{ marginTop: 15 }}>{labels.article.label.replaceAll("${plan}", StringHelper.toSaneSentenceFormat(planName))}</a>
            </div>),
            onConfirm: async (button) => {
                button?.setLoadingState(true);
                ChangePlan.PlatformService.queryPlans(profile.extra_data?.currency_code ?? "NOT_SET").then(({ apiResponse }: SanitizedResponse<IPlatformPlan>) => {
                    const currentPlanIndex = apiResponse.data!.content?.findIndex((p) => p.code === planName) ?? 0;
                    const platformPlan = apiResponse.data!.content?.[currentPlanIndex];
                    if (!platformPlan) {
                        Siren.alert(labels.no_plan.replaceAll("${plan}", planName));
                        return;
                    }
                    ChangePlan.MemberService.queryPaymentMethods(profile.extra_data.currency_code).then(({ apiResponse }: SanitizedResponse<IPaymentMethod>) => {
                        const activeDialogMutableRoot = { current: null } as any;
                        ChangePlan.to({
                            profile,
                            activeDialogMutableRoot,
                            onSuccess: window.location.reload,
                            paymentMethods: apiResponse.data?.content,
                        }, "UPGRADE", platformPlan, platformPlan.__calculated_amounts?.[(profile.extra_data?.current_plan_duration as any ?? "MONTHLY").toLowerCase() as ("yearly" | "monthly")].amount, "info");
                    }).catch(BaseService.reportError);
                }).catch(BaseService.reportError);
                return true;
            },
            onCancel: cancelAction
        });
        dialog.show();
    }

    static to(options: {
        profile: IMember;
        onClose?: () => void;
        onSuccess: () => void;
        duration?: "MONTHLY" | "YEARLY";
        paymentMethods?: IPaymentMethod[];
        updateProfile?: (...data: any) => void;
        activeDialogMutableRoot: React.RefObject<ReactDOM.Root | null>;
    }, change: "UPGRADE" | "DOWNGRADE", platformPlan: IPlatformPlan, amount: number, step: string, selectedPaymentMethod?: IPaymentMethod, factorId?: string) {
        let value: string = "";
        let content: NoseurElement;
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = (ChangePlan.Labels.dialog as any)[change?.toLowerCase()];

        if (!options.duration) options.duration = options.profile.extra_data?.current_plan_duration as any ?? "MONTHLY";
        const buildPaymentMethod = (paymentMethod: IPaymentMethod, isSelected: boolean, onClick?: () => void) => {
            const { details } = paymentMethod;
            const type = paymentMethod.type?.toLowerCase();
            const brand = (paymentMethod.details?.card_brand ?? type).toLowerCase();

            return (<div key={paymentMethod.external_id} className={Classname.build("pm", { "active": isSelected })} onClick={onClick}>
                {type === "card" && (<>
                    <img alt={"type"} src={Utils.paymentMethodLogoMap(brand)} className="method-logo" />
                    <div>
                        <span className="name">{details.name} {paymentMethod.is_default && (<span className="noseur-pill tf-pill-omnehost">preferred</span>)}</span>
                        <span className="m-card">{Utils.addSeparatorAtEveryDivisor(details.masked_card_number, 4)}</span>
                    </div>
                </>)}
                {type === "payment_link" && (<>
                    <span className="others">{(dialogLabels as any).others}</span>
                    <div className="ots">
                        <img alt={"payment link"} src={Utils.paymentMethodLogoMap("payment_link")} className="method-logo" />
                        <img alt={"card"} src={Utils.paymentMethodLogoMap("unknown")} className="method-logo" />
                        <i className="fa fa-mobile-alt" />
                    </div>
                </>)}
                {isSelected && (<i className="fa fa-check" />)}
            </div>);
        };
        if (step === "info") {
            content = ChangePlan.buildPlanChangeBaseView(dialogLabels, dialogErrorRef);
        } else if (step === "payment_method") {
            const updatePaymentMethodWithSelected = (isDefaultId?: string) => {
                if (!options.activeDialogMutableRoot.current) return;
                const pms = options.paymentMethods?.map((paymentMethod) => {
                    const isSelected = (isDefaultId === paymentMethod.external_id || (!isDefaultId && paymentMethod.is_default));
                    if (!selectedPaymentMethod && paymentMethod.is_default) selectedPaymentMethod = paymentMethod;

                    return buildPaymentMethod(paymentMethod, isSelected, () => {
                        selectedPaymentMethod = paymentMethod;
                        if (isSelected) return;
                        updatePaymentMethodWithSelected(paymentMethod.external_id);
                    });
                });
                options.activeDialogMutableRoot.current.render(pms);
            };
            content = ChangePlan.buildPlanChangeBaseView(dialogLabels, dialogErrorRef, (<div className="change-plan-select-pm">
                <span className="t">{(dialogLabels as any).select_payment_method}</span>
                <div className="pms" ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(options.activeDialogMutableRoot, ReactDOM.createRoot(r));
                    updatePaymentMethodWithSelected(selectedPaymentMethod?.external_id);
                }}>
                </div>
            </div>));
        } else if (step === "enter_token") {
            if (!selectedPaymentMethod) {
                content = ChangePlan.buildPlanChangeBaseView(dialogLabels, dialogErrorRef, ChangePlan.buildPlanTokenView((v) => {
                    value = v;
                    dialogErrorRef.current.innerText = " ";
                }));
            } else {
                content = ChangePlan.buildPlanChangeBaseView(dialogLabels, dialogErrorRef, (<>
                    <div className="change-plan-select-pm">
                        <span className="t">{ChangePlan.Labels.dialog.upgrade_plan_using} - <span className="link" onClick={() => {
                            dialog.destroy(() => ChangePlan.to(options, change, platformPlan, amount, "payment_method", selectedPaymentMethod));
                        }}>{ChangePlan.Labels.dialog.change_payment_method}</span></span>
                        <div className="pms">
                            {buildPaymentMethod(selectedPaymentMethod!, true)}
                        </div>
                    </div>
                    {ChangePlan.buildPlanTokenView((v) => {
                        value = v;
                        dialogErrorRef.current.innerText = " ";
                    })}
                </>));
            }
        }
        const requestPlanChange = (paymentMethodId?: string) => ChangePlan.MemberService.requestPlanChange({
            type: change,
            plan: platformPlan.code,
            duration: options.duration!,
            payment_method_id: paymentMethodId,
        });
        const dialog = ConfirmDialog({
            content,
            closable: true,
            title: dialogLabels.title,
            style: { maxWidth: "500px" },
            confirmScheme: (change === "DOWNGRADE" ? Scheme.DANGER : Schemes.OMNEHOST),
            desc: ObjectHelper.expandStringTemplate(dialogLabels.desc, {
                plan: platformPlan.name,
                currency: i18nManager.getCurrencySign(options.profile.extra_data?.currency_code),
                amount: StringHelper.toPresentableMoneyValue(amount),
                cost_info: ObjectHelper.expandStringTemplate((dialogLabels.cost_info ?? ""), {
                    currency: i18nManager.getCurrencySign(options.profile.extra_data?.currency_code),
                    amount: StringHelper.toPresentableMoneyValue(amount),
                }, { chop: "$" })
            }, { chop: "$" }),
            onConfirm: async (button?: ButtonManageRef) => {
                if (step === "error") {
                    ChangePlan.to(options, change, platformPlan, amount, "info");
                    options.onClose?.();
                    return true;
                }
                let stopLoadingAtEnd = true;
                button?.setLoadingState(true);
                try {
                    if (step === "info" && amount > 0) {
                        dialog.destroy(() => ChangePlan.to(options, change, platformPlan, amount, "payment_method", selectedPaymentMethod));
                    } else if ((step === "info" && amount === 0) || step === "payment_method") {
                        const { sanitized } = await requestPlanChange(selectedPaymentMethod?.external_id);
                        dialog.destroy(() => ChangePlan.to(options, change, platformPlan, amount, "enter_token", selectedPaymentMethod, sanitized.email_factor_id));
                    } else if (step === "enter_token") {
                        const { status, sanitized } = await ChangePlan.MemberService.makePlanChangePayment({
                            value,
                            factor_id: factorId!
                        });
                        if (status === 204) {
                            dialog.destroy(() => {
                                Siren.alert(dialogLabels.success_message.replace("${plan}", platformPlan.name).replace("${duration}", options.duration));
                                options.onClose?.();
                                ChangePlan.MemberService.getProfile().then(({ sanitized }: SanitizedResponse<IMember>) => {
                                    options.updateProfile?.({ ...options.profile, plan: platformPlan.code as OmneHostPlan, extra_data: { ...options.profile.extra_data, current_plan_duration: options.duration } });
                                }).catch(BaseService.reportError).finally(options.onSuccess);
                            });
                        } else if (sanitized.payment_redirect_url) {
                            stopLoadingAtEnd = false;
                            window.location.href = sanitized.payment_redirect_url;
                        }
                    }
                } catch (error: any) {
                    const errorMessage = error.errorMessage ?? error.message;
                    if (step === "enter_token" && change === "UPGRADE") {
                        if (error.response.status === 403) {
                            dialog.destroy(() => ChangePlan.to(options, change, platformPlan, amount, "payment_method", selectedPaymentMethod));
                        } else if (dialogErrorRef.current) {
                            dialogErrorRef.current.innerText = errorMessage;
                        }
                    } else {
                        step = "error";
                        dialog.update({
                            desc: "",
                            reverseControlActions: true,
                            confirmLabel: i18nManager.Labels.common.close,
                            cancelLabel: i18nManager.Labels.common.try_again,
                            content: ChangePlan.buildPlanSuccessErrorView(platformPlan.name, "upgrade", errorMessage as string),
                        });
                    }
                }
                if (stopLoadingAtEnd) button?.setLoadingState(false);
                return false;
            },
            onCancel: () => {
                options.onClose?.();
            }
        });
        dialog.show();
    }

    private static buildPlanChangeBaseView(dialogLabels: any, dialogErrorRef: any, newContent?: any) {
        return (<div className="plan-change" style={{ margin: "15px 0px 5px 0px" }}>
            {dialogLabels.desc2}
            <a href={dialogLabels.article.location} target="_blank" rel={"noreferrer"} style={{ marginTop: 15 }}>{dialogLabels.article.label}</a>
            {newContent}
            <div ref={dialogErrorRef} className="error"> </div>
        </div>);
    }

    private static buildPlanTokenView(onInput: (v: string) => void) {
        return (<div style={{ marginTop: 20 }}>
            <span>{ChangePlan.Labels.plan_change_token_sent}</span>
            <FormControl style={{ marginTop: 15 }} isFieldset fill label={i18nManager.Labels.common.token}>
                <TextInput onInputComplete={onInput} />
            </FormControl>
        </div>);
    }

    private static buildPlanSuccessErrorView(plan: string, change: "upgrade" | "downgrade", errorMessage: string) {
        return (<div style={{ marginTop: 20 }} className="plan-change-rept">
            <i className="fa fa-exclamation-circle" />
            <span dangerouslySetInnerHTML={{
                __html: ObjectHelper.expandStringTemplate(ChangePlan.Labels.report_message, {
                    plan,
                    error_message: `<span style="color: #FF7171;">${errorMessage}</span>`,
                    change: (change === "upgrade" ? ChangePlan.Labels.upgrade : ChangePlan.Labels.downgrade),
                }, { chop: "$" })
            }} />
        </div>);
    }

}

export default ManagePlan;
